import { environment } from "./constants";

type KlineSubscribeParams = {
  symbol?: string;
  interval?: string;
  params?: Record<string, any>;
  exchange?: string;
  exchangeType?: string;
  limit?: number;
};

type KlineCallback = (data: any) => void;

type SubscriptionKey = string;

function getSubscriptionKey({
  symbol,
  interval,
  params,
  exchange,
  exchangeType,
}: KlineSubscribeParams): SubscriptionKey {
  return [
    exchange,
    exchangeType,
    symbol,
    interval,
    JSON.stringify(params || {}),
  ].join(":");
}

class WebSocketKlineConnection {
  private ws: WebSocket | null = null;
  private endpoint: string;
  private pingInterval: any = null;
  private reconnectTimeout: any = null;
  private isManuallyClosed = false;
  private subscribers: Map<SubscriptionKey, Set<KlineCallback>> = new Map();
  private subscribeMessages: Map<SubscriptionKey, any> = new Map();
  private unsubscribeMessages: Map<SubscriptionKey, any> = new Map();

  constructor(endpoint: string) {
    this.endpoint = endpoint;
    this.connect();
  }

  private connect() {
    this.ws = new WebSocket(this.endpoint);
    this.ws.onopen = () => {
      this.startPing();
      // Resubscribe to all active subscriptions
      for (const [key, msg] of this.subscribeMessages.entries()) {
        this.send(msg);
      }
    };
    this.ws.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }
      // Route to all matching subscribers
      for (const [key, callbacks] of this.subscribers.entries()) {
        callbacks.forEach((cb) => cb(data));
      }
    };
    this.ws.onerror = () => {
      this.reconnect();
    };
    this.ws.onclose = () => {
      this.stopPing();
      if (!this.isManuallyClosed) {
        this.reconnect();
      }
    };
  }

  private reconnect() {
    if (this.reconnectTimeout) {
      return;
    }
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.connect();
    }, 3000);
  }

  private startPing() {
    this.stopPing();
    this.pingInterval = setInterval(() => {
      this.send({ method: "PING" });
    }, 30000);
  }

  private stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private send(msg: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  /**
   * Requests a synthetic topic name from the WebSocket server.
   * Resolves with the topic name from the first response containing a 'topic' field.
   */
  getSyntheticTopicWS(
    symbol: string,
    timeFrame: string,
    exchange: string,
    exchangeType: string,
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const msg = {
        method: "GET",
        path: "/synthetic/live",
        id: symbol,
        params: { symbol, timeFrame, exchange, exchangeType },
      };

      const handleMessage = (data: any) => {
        if (data && data.topic) {
          this.ws?.removeEventListener("message", onMessage);
          resolve(data.topic);
        } else if (data && data.result && data.result.topic) {
          this.ws?.removeEventListener("message", onMessage);
          resolve(data.result.topic);
        }
      };

      const onMessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          if (data.id === symbol) {
            handleMessage(data);
          }
        } catch {
          console.error("Error parsing synthetic topic message", event.data);
        }
      };

      this.ws?.addEventListener("message", onMessage);
      this.send(msg);

      setTimeout(() => {
        this.ws?.removeEventListener("message", onMessage);
        reject(new Error("Timeout waiting for synthetic topic"));
      }, 5000);
    });
  }

  /**
   * Stops a synthetic stream by topic name via WebSocket.
   */
  stopSyntheticTopicWS(topic: string) {
    this.send({
      method: "GET",
      path: "/synthetic/live/stop",
      params: { topic },
    });
  }

  subscribe(
    correlationId: string,
    params: KlineSubscribeParams,
    callback: KlineCallback,
    subscribeMessage?: (params: KlineSubscribeParams) => any,
    unsubscribeMessage?: (params: KlineSubscribeParams) => any,
  ) {
    const key = getSubscriptionKey(params);
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, new Set());
      // Send subscribe message
      const isSynthetic =
        params.exchangeType === "SYNTHETIC" || params.exchange === "SYNTHETIC";
      const msg = subscribeMessage
        ? subscribeMessage(params)
        : {
            method: "GET",
            path: isSynthetic ? "/synthetic" : "/kline",
            id: correlationId,
            params: {
              exchange: params.exchange || "BYBIT",
              exchangeType: params.exchangeType || "FUTURES",
              symbol: params.symbol,
              timeFrame: params.interval,
              limit: params.limit || 1000,
              ...params.params,
            },
          };
      this.subscribeMessages.set(key, msg);
      this.send(msg);
      if (unsubscribeMessage) {
        this.unsubscribeMessages.set(key, unsubscribeMessage(params));
      }
    }
    this.subscribers.get(key)!.add(callback);
    return () => this.unsubscribe(params, callback);
  }

  unsubscribe(params: KlineSubscribeParams, callback: KlineCallback) {
    const key = getSubscriptionKey(params);
    const callbacks = this.subscribers.get(key);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        // Send unsubscribe message if needed
        const msg = this.unsubscribeMessages.get(key);
        if (msg) {
          this.send(msg);
        }
        this.subscribers.delete(key);
        this.subscribeMessages.delete(key);
        this.unsubscribeMessages.delete(key);
      }
    }
  }

  close() {
    this.isManuallyClosed = true;
    this.stopPing();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  isConnected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

// Singleton instance (replace endpoint with your actual WS endpoint)
const endpoint = environment.websocketKlineUrl;
export const websocketKlineConnection = new WebSocketKlineConnection(endpoint);
